import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ConfigService, IUtilsDictionary, LoggingService, UtilsJson, UtilsMisc, UtilsString } from '@pit/core';
import { EntityDataService } from '@pit/core-data';
import { DtoDevice, DtoMandator, IDtoDevice, IDtoMandator } from '@pit/frontend-model';
import { map, Observable } from 'rxjs';
import { IDtoDeviceEntityDataService } from '../interfaces/dto.device.entity.data.service.interface';

@Injectable({
  providedIn: 'root'
})
export class DtoDeviceEntityDataService extends EntityDataService<IDtoDevice> implements IDtoDeviceEntityDataService {

  constructor(http: HttpClient, configService: ConfigService, loggingService: LoggingService) {
    super(http, null, configService, loggingService);
  }

  public getDeviceByDeviceId$(deviceId: string): Observable<IDtoDevice> {
    return this.httpGet(`/api/web/devices/${deviceId}`)
      .pipe(map((json: IDtoDevice) => {
        let result: IDtoDevice = null;
        if (UtilsMisc.isNullOrUndefined(json) === false) {
          result = this.createEntityFromJson(json);
        }
        return result;
      }));
  }

  public async getDeviceByDeviceIdAsync(deviceId: string): Promise<IDtoDevice> {
    return (await this.getDeviceByDeviceId$(deviceId).toPromise());
  }

  public getDevicesForMandatorByDepotNumber$(mandator: string, depotNumber: string, onlyWithShipments: boolean = false): Observable<IDtoDevice[]> {
    return this.httpGet(`/api/web/devices/mandator/${mandator}/depot/${depotNumber}/${onlyWithShipments}`)
      .pipe(map((entities: IDtoDevice[]) => {
        const result: IDtoDevice[] = [];
        if ((UtilsMisc.isNullOrUndefined(entities) === false) && (entities.length > 0)) {
          for (const entity of entities) {
            result.push(this.createEntityFromJson(entity));
          }
        }
        return result;
      }));
  }

  public async getDevicesForMandatorByDepotNumberAsync(mandator: string, depotNumber: string, onlyWithShipments: boolean = false): Promise<IDtoDevice[]> {
    return (await this.getDevicesForMandatorByDepotNumber$(mandator, depotNumber, onlyWithShipments).toPromise());
  }

  public logoutDevice$(deviceId: string): Observable<boolean> {
    return this.httpPost(`/api/web/devices/${deviceId}/logout`, null)
      .pipe(map((json: boolean) => {
        return json;
      }));
  }

  public async logoutDeviceAsync(deviceId: string): Promise<boolean> {
    return (await this.logoutDevice$(deviceId).toPromise());
  }

  public resetDevice$(deviceId: string): Observable<boolean> {
    return this.httpPost(`/api/web/devices/${deviceId}/reset`, null)
      .pipe(map((json: boolean) => {
        return json;
      }));
  }

  public async resetDeviceAsync(deviceId: string): Promise<boolean> {
    return (await this.resetDevice$(deviceId).toPromise());
  }

  public releaseDevice$(deviceId: string, userName: string = null): Observable<IDtoDevice> {
    return this.http.post(`/api/web/devices/${deviceId}/release`, null, { headers: this.buildUserNameHeaders(userName) })
      .pipe(map((json: IDtoDevice) => {
        let result: IDtoDevice = null;
        if (UtilsMisc.isNullOrUndefined(json) === false) {
          result = this.createEntityFromJson(json);
        }
        return result;
      }));
  }

  public async releaseDeviceAsync(deviceId: string, userName: string = null): Promise<IDtoDevice> {
    return (await this.releaseDevice$(deviceId, userName).toPromise());
  }

  public assignMandatorToDevice$(deviceId: string, mandatorId: number, userName: string = null): Observable<IDtoDevice> {
    return this.http.post(`/api/web/devices/${deviceId}/mandator/${mandatorId}`, null, { headers: this.buildUserNameHeaders(userName) })
      .pipe(map((json: IDtoDevice) => {
        let result: IDtoDevice = null;
        if (UtilsMisc.isNullOrUndefined(json) === false) {
          result = this.createEntityFromJson(json);
        }
        return result;
      }));
  }

  public async assignMandatorToDeviceAsync(deviceId: string, mandatorId: number, userName: string = null): Promise<IDtoDevice> {
    return (await this.assignMandatorToDevice$(deviceId, mandatorId, userName).toPromise());
  }

  private buildUserNameHeaders(userName: string): HttpHeaders {
    let headers: HttpHeaders = new HttpHeaders();
    let effectiveUserName: string = userName;
    if (UtilsString.stringIsNullOrEmpty(effectiveUserName) === true) {
      effectiveUserName = this.configurationService?.configAuth?.userName;
    }
    if (UtilsString.stringIsNullOrEmpty(effectiveUserName) === false) {
      headers = headers.set('X-User-Name', effectiveUserName);
    }
    return headers;
  }

  public getAllMandators$(): Observable<IDtoMandator[]> {
    return this.httpGet(`/api/web/devices/mandators`)
      .pipe(map((entities: IDtoMandator[]) => {
        const result: IDtoMandator[] = [];
        if ((UtilsMisc.isNullOrUndefined(entities) === false) && (entities.length > 0)) {
          for (const entity of entities) {
            result.push(UtilsJson.deserialize(entity, DtoMandator) as IDtoMandator);
          }
        }
        return result;
      }));
  }

  public async getAllMandatorsAsync(): Promise<IDtoMandator[]> {
    return (await this.getAllMandators$().toPromise());
  }

  protected override doGetApiUrl(): string {
    return UtilsString.stringFormat('/api/web/devices', this.configurationService.configData.urlApi);
  }

  protected override createEntityFromJson(json: any): IDtoDevice {
    const result: IDtoDevice = (UtilsJson.deserialize(json, DtoDevice) as IDtoDevice);
    if (result !== null) {
      result.setReferences();
    }

    return result;
  }

  protected override doGetUrls(): IUtilsDictionary<string> {
    this._urls = super.doGetUrls();

    this._urls.add('get.by.deviceid', UtilsString.stringFormat('{0}://{1}:{2}/{3}/', this.configurationService.configData.protocol, this.configurationService.configData.ip, this.configurationService.configData.port, this.apiUrl) + '{0}');
    this._urls.add('get.by.depot', UtilsString.stringFormat('{0}://{1}:{2}/{3}/', this.configurationService.configData.protocol, this.configurationService.configData.ip, this.configurationService.configData.port, this.apiUrl) + 'mandator/{0}/depot/{1}/{2}');
    this._urls.add('logout', UtilsString.stringFormat('{0}://{1}:{2}/{3}/', this.configurationService.configData.protocol, this.configurationService.configData.ip, this.configurationService.configData.port, this.apiUrl) + '{0}');
    this._urls.add('reset', UtilsString.stringFormat('{0}://{1}:{2}/{3}/', this.configurationService.configData.protocol, this.configurationService.configData.ip, this.configurationService.configData.port, this.apiUrl) + '{0}');

    return this._urls;
  }

}
